<template>
  <div class="line-chart-viz" />
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import * as d3 from 'd3';

am4core.addLicense('CH245905005');
am4core.addLicense('TL245443410');
am4core.useTheme(am4themesAnimated);

export default {
  name: 'LineChartViz',
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/line-chart-data.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();

    dataSource.events.on('done', ({ data }) => {
      // prepare data
      this.sanitisedData = data;

      const dataByRegion = d3.group(data, (d) => d.country);
      this.dataByRegionArr = Array.from(dataByRegion, ([key, value]) => ({ key, value }));

      // create chart
      this.chart = this.createChart(data);

      // create axes
      this.valueAxis = this.createAxisY();
      this.categoryAxis = this.createAxisX();

      // create series
      const lines = ['#E25A5F', '#FDC4D0', '#005EB8', '#F09252', '#F6C76A'];
      lines.forEach((color, i) => this.createSeries(color, i));
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    createChart(data) {
      // create XY chart
      const chart = am4core.create(this.$el, am4charts.XYChart);

      // settings
      chart.padding(10, 25, 0, 0);
      if (this.$breakpoint.mobile) {
        chart.padding(10, 38, 0, 0);
      }

      chart.zoomOutButton.disabled = true;
      chart.data = data;
      return chart;
    },
    createAxisY() {
      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.labels.template.fill = '#595959';
      valueAxis.renderer.grid.template.stroke = '#595959';
      valueAxis.title.text = 'Share of funding (%)';
      valueAxis.title.fill = '#595959';

      return valueAxis;
    },
    createAxisX() {
      const categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'year';
      // categoryAxis.renderer.minGridDistance = 10;
      // categoryAxis.renderer.grid.template.location = 0.5;
      categoryAxis.startLocation = 0.2;
      categoryAxis.renderer.grid.template.strokeWidth = 0;
      categoryAxis.endLocation = 0.8;
      categoryAxis.renderer.ticks.template.length = 10;
      categoryAxis.renderer.labels.template.fill = '#595959';
      categoryAxis.renderer.labels.template.wrap = true;
      categoryAxis.renderer.labels.template.maxWidth = 80;
      categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.minGridDistance = 20;
      }

      return categoryAxis;
    },
    createSeries(color, index) {
      const series = this.chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = 'percentage';
      series.dataFields.categoryX = 'year';
      series.dataFields.country = 'country';
      series.dataFields.actual = 'value';
      series.data = this.dataByRegionArr[index].value;
      series.strokeWidth = 2;
      series.stroke = color;
      series.tensionX = 0.8;
      series.tensionY = 0.8;

      const circleBullet = series.bullets.push(new am4charts.CircleBullet());
      circleBullet.circle.fill = color;
      circleBullet.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      circleBullet.tooltip = new am4core.Tooltip();
      circleBullet.tooltipHTML = `<p class="text-legend font-weight-medium" style="margin-bottom: 10px;">{country} ({categoryX})</p><p class="text-small" style="margin-bottom: 0px"><span class="font-weight-medium">Share of funding:</span> {valueY}%</p><p class="text-small" style="margin-bottom: 5px"><span class="font-weight-medium">Funding:</span> US\${actual}B</p>`;

      circleBullet.tooltip.background.cornerRadius = 4;
      circleBullet.tooltip.getFillFromObject = false;
      circleBullet.tooltip.background.fill = '#FFFFFF';
      circleBullet.tooltip.label.fill = '#373839';
      circleBullet.tooltip.background.pointerLength = 0;
      circleBullet.tooltip.label.maxWidth = 200;
      circleBullet.tooltip.label.wrap = true;
      circleBullet.circle.radius = 5;

      if (this.$breakpoint.mobile) {
        circleBullet.circle.radius = 7;
        circleBullet.tooltip.label.maxWidth = 150;
        circleBullet.tooltipHTML = `
          <p class="font-weight-medium" style="margin-bottom: 10px; font-size: 15px !important">
            {country} <br> ({categoryX})
          </p>
          <p style="margin-bottom: 0px; font-size: 14px !important">
            <span class="font-weight-medium">
              Share of funding:
            </span>
            <br>{valueY}%
          </p>
          <p style="margin-bottom: 5px; font-size: 14px !important">
            <span class="font-weight-medium">Funding:</span> US\${actual}B
          </p>`;
        circleBullet.tooltip.padding(0.5, 0.5, 0.5, 0.5);
      }

      circleBullet.tooltip.background.fillOpacity = 1;
      circleBullet.tooltip.background.cornerRadius = 10;
      circleBullet.tooltip.dy = -10;
      series.bulletsContainer.parent = this.chart.seriesContainer;
    },
  },
};
</script>

<style lang="scss" scoped>
.line-chart-viz {
  width: 100%;
  height: 380px;
}
</style>
